import { ErrorMessage } from "@atlaskit/form";
import { currencyFormatter } from "@fla/common";
import {
  AssetType,
  BusinessBaseProps,
  Business_InitialState,
  Button,
  ConsentOrdersPathName,
  FormNavigation,
  RadioSelect,
  useBusiness
} from "@fla/components";
import { BusinessBaseSchema, useNames } from "@fla/components";
import BusinessIcon from "@fla/components/src/assets/svgs/business.svg";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "app/store";
import { withConsentOrder } from "components/HOCs/withConsentOrder";
import { useConsentOrders } from "hooks/ConsentOrders";
import { SliceStatus } from "interfaces";
import { consentOrdersActions, selectAsset, selectBaseAssetsDebts, selectConsentOrdersBase } from "pages/ConsentOrders";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

const defaultValues = {
  if_own_business: ""
};

function Business() {
  const businessData = useSelector(selectAsset(AssetType.BUSINESS)) as Business_InitialState[];
  const { initialDetails } = useSelector(selectConsentOrdersBase);
  const usenames = useNames({ initialDetails });
  const { spouseFirstName } = usenames;
  const assetsDebts = useSelector(selectBaseAssetsDebts);
  const [status, setStatus] = useState(SliceStatus.Idle);
  const [error, setError] = useState<string>();

  const location = useLocation();
  const navigate = useNavigate();
  const { handleSubmit, watch, control, reset, getValues } = useForm({
    mode: "all",
    defaultValues,
    resolver: yupResolver(BusinessBaseSchema)
  });

  const { ownCard, splitCard, userTotalAmount, spouseTotalAmount } = useBusiness({ businessData, ...usenames });

  const handleBackClick = () => {
    navigate(ConsentOrdersPathName.Investments);
  };

  const editData = (values?: BusinessBaseProps) => {
    navigate(`${ConsentOrdersPathName.Business}/add`, {
      state: {
        parent: getValues(),
        child: values
      }
    });
  };

  const dispatch = useAppDispatch();
  const { updateAssetsDebts } = useConsentOrders();

  const onSubmit = async (values: BusinessBaseProps) => {
    try {
      setStatus(SliceStatus.Pending);

      if (values.if_own_business === "no" && businessData.length) {
        setError("If neither of you have any business in Australia, then please remove the added business details.");
      } else if (values.if_own_business === "yes" && !businessData.length) {
        setError("please add business details.");
      } else {
        setError("");

        const payload = {
          business: { data: values, isCompleted: true },
          currentStep: 7
        };
        await updateAssetsDebts(payload);
        dispatch(consentOrdersActions.setAssetsDebts(payload));
        navigate(ConsentOrdersPathName.Overseas);
      }
      setStatus(SliceStatus.Resolved);
    } catch (error) {
      setStatus(SliceStatus.Rejected);
    }
  };

  useEffect(() => {
    if (location.state) {
      reset(location.state);
    } else {
      reset(assetsDebts?.business?.data);
    }
  }, [location]);

  return (
    <section className="w-full py-10">
      <form
        id="business-form"
        className="md:w-10/12 lg:max-w-2xl mx-auto h-fit px-5 md:px-10 md:py-5 space-y-5"
        onSubmit={handleSubmit(onSubmit)}
      >
        <img src={BusinessIcon} className="w-10 h-auto mx-auto my-3" alt="business" />

        <h1 className="text-center my-5">Business/Trust</h1>

        <RadioSelect
          title={`Do you or ${spouseFirstName} own an businesses or a beneficiary of a trust in Australia?`}
          id="if_own_business"
          name="if_own_business"
          control={control}
          options={[
            { label: "Yes", value: "yes" },
            { label: "No", value: "no" }
          ]}
          type="card"
          onChange={() => setError("")}
          theme="dark"
        />

        <div className="w-full py-10 border border-gray-400 border-dashed rounded-lg flex flex-col items-center justify-center space-y-10 mb-5">
          <p className="px-16 text-center text-base font-bold">
            Please add all the businesses or trusts you and {spouseFirstName}
            &nbsp;have in Australia
          </p>
          <Button
            className="px-5 py-2 uppercase"
            fontSize="sm"
            onClick={() => editData(undefined)}
            disabled={watch("if_own_business") !== "yes"}
          >
            + Add Business/Trust
          </Button>
        </div>

        {error ? (
          <div className="max-w-sm mx-auto text-center">
            <ErrorMessage>{error}</ErrorMessage>
          </div>
        ) : null}

        {Array.isArray(businessData) && businessData.length ? (
          <>
            <p className="uppercase text-gray-500 text-xl font-medium my-5">You’re Keeping</p>

            {ownCard("user", editData)}
            {splitCard("user", editData)}
            <section className="flex justify-end items-center mb-12">
              <p className="text-gray-600">
                Total:
                <span className="text-2xl ml-2">{currencyFormatter.format(userTotalAmount)}</span>
              </p>
            </section>

            <p className="uppercase text-gray-500 text-xl font-medium my-5">{spouseFirstName} Keeping</p>

            {ownCard("spouse", editData)}
            {splitCard("spouse", editData)}
            <section className="flex justify-end items-center mb-12">
              <p className="text-gray-600">
                Total:
                <span className="text-2xl ml-2">{currencyFormatter.format(spouseTotalAmount)}</span>
              </p>
            </section>
          </>
        ) : null}

        <FormNavigation
          isFirst={false}
          isLast={false}
          form="business-form"
          handleBackClick={handleBackClick}
          nextButtonText="Continue"
          isSubmitting={status === SliceStatus.Pending}
          classes="mt-12"
        />
      </form>
    </section>
  );
}
export default withConsentOrder(Business);
